import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from './api';
import type { Recipe } from '../data/types';

// Cook progress — which steps and ingredients have been crossed off. Stored
// server-side like servings, so the phone propped on the counter and the
// laptop in the next room show the same ledger. The server drops it after a
// day so a half-cooked recipe never haunts the next cook.
//
// Two independent sets, one derived rule:
//   a step is done      when it is in `steps`
//   an ingredient is done when it is in `ingredients`, OR every step that
//                       uses it is done (crossing off a step crosses off
//                       what it consumed)

export type ProgressEntry = { steps: number[]; ingredients: string[] };

type State = {
  entries: Record<string, ProgressEntry | undefined>;
  update: (slug: string, fn: (prev: ProgressEntry) => ProgressEntry) => void;
  clear: (slug: string) => void;
};

const EMPTY: ProgressEntry = { steps: [], ingredients: [] };
const Ctx = createContext<State>({ entries: {}, update: () => {}, clear: () => {} });

export function ProgressProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<Record<string, ProgressEntry | undefined>>({});
  const latest = useRef(entries);
  latest.current = entries;
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    api.progress().then(setEntries).catch(() => {});
  }, []);

  // Taps come in bursts (cross off four ingredients in a row); one write per
  // burst is plenty, and the last state always wins.
  function flush(slug: string) {
    clearTimeout(timers.current[slug]);
    timers.current[slug] = setTimeout(() => {
      const e = latest.current[slug];
      const p = e && (e.steps.length || e.ingredients.length) ? api.setProgress(slug, e.steps, e.ingredients) : api.clearProgress(slug);
      p.catch(() => {});
    }, 350);
  }

  function update(slug: string, fn: (prev: ProgressEntry) => ProgressEntry) {
    setEntries((o) => ({ ...o, [slug]: fn(o[slug] ?? EMPTY) }));
    flush(slug);
  }

  function clear(slug: string) {
    setEntries((o) => ({ ...o, [slug]: undefined }));
    flush(slug);
  }

  return <Ctx.Provider value={{ entries, update, clear }}>{children}</Ctx.Provider>;
}

function toggle<T>(list: T[], v: T): T[] {
  return list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
}

// Everything the recipe page and cook mode need to render one recipe's checks.
export function useRecipeProgress(recipe: Recipe | undefined) {
  const { entries, update, clear } = useContext(Ctx);
  const slug = recipe?.slug ?? '';
  const entry = entries[slug] ?? EMPTY;
  const steps = new Set(entry.steps);
  const ticked = new Set(entry.ingredients);

  const isStepDone = (n: number) => steps.has(n);
  const isUseDone = (id: string, step: number) => ticked.has(id) || steps.has(step);
  const isIngredientDone = (id: string) => {
    if (ticked.has(id)) return true;
    const ing = recipe?.ingredients.find((i) => i.id === id);
    return !!ing && ing.uses.every((u) => steps.has(u.step));
  };

  return {
    isStepDone,
    isUseDone,
    isIngredientDone,
    doneSteps: steps.size,
    anyDone: steps.size > 0 || ticked.size > 0,
    toggleStep: (n: number) => recipe && update(slug, (p) => ({ ...p, steps: toggle(p.steps, n) })),
    markStepDone: (n: number) =>
      recipe && update(slug, (p) => (p.steps.includes(n) ? p : { ...p, steps: [...p.steps, n] })),
    toggleIngredient: (id: string) => {
      if (!recipe) return;
      // If it reads as done only because its steps are done, a tap should
      // un-cross it: drop it from the ticked list AND un-check those steps.
      update(slug, (p) => {
        const ing = recipe.ingredients.find((i) => i.id === id);
        const viaSteps = !!ing && ing.uses.every((u) => p.steps.includes(u.step));
        if (p.ingredients.includes(id)) {
          return { steps: viaSteps ? p.steps.filter((n) => !ing!.uses.some((u) => u.step === n)) : p.steps, ingredients: p.ingredients.filter((x) => x !== id) };
        }
        if (viaSteps) {
          return { steps: p.steps.filter((n) => !ing!.uses.some((u) => u.step === n)), ingredients: p.ingredients };
        }
        return { ...p, ingredients: [...p.ingredients, id] };
      });
    },
    clear: () => recipe && clear(slug),
  };
}
